/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed,
 * sublicensed, or used without written permission of Paxia AI Pty Ltd.
 */

import React from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { Account } from '../../types';
import type { BuilderOperatingProfile } from '../../types/agent';

/**
 * Compact operating-controls strip for the auto-trading creation proof.
 *
 * Account and symbol are real user selections; Auto trading is the only mode
 * of this proof and is shown preselected. Every fixed control value (risk
 * allocation, maximum, reward:risk, action, preflight, installation) is READ
 * from the backend operating-profile owner — this component never restates
 * the values as local literals.
 */

export interface ABVOperatingControlsProps {
  accounts: Account[];
  accountsLoading?: boolean;
  profile: BuilderOperatingProfile | null;
  accountId: string | null;
  symbol: string | null;
  onSelect: (selection: { accountId: string; symbol: string }) => void;
  disabled?: boolean;
}

const ABVOperatingControls: React.FC<ABVOperatingControlsProps> = ({
  accounts,
  accountsLoading = false,
  profile,
  accountId,
  symbol,
  onSelect,
  disabled = false,
}) => {
  const selectedAccount = accounts.find(account => account.id === accountId) ?? null;
  const symbols = profile?.supported_symbols ?? [];
  const effectiveSymbol = symbol && symbols.includes(symbol) ? symbol : (symbols[0] ?? null);
  const settings = profile?.settings;

  const chooseAccount = (account: Account | null) => {
    if (!account || !effectiveSymbol || disabled) return;
    onSelect({ accountId: account.id, symbol: effectiveSymbol });
  };
  const chooseSymbol = (value: string) => {
    if (!value || !accountId || disabled) return;
    onSelect({ accountId, symbol: value });
  };

  return (
    <Box
      data-testid="abv-operating-controls"
      sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5,
        border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, mb: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Autocomplete
        data-testid="abv-account-select"
        size="small"
        sx={{ minWidth: 260 }}
        options={accounts}
        value={selectedAccount}
        onChange={(_event, value) => chooseAccount(value)}
        getOptionLabel={option => `${option.name} (${option.platform})`}
        renderOption={(props, option) => {
          const { key, ...rest } = props as any;
          return (
            <li key={key} {...rest}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <Typography variant="body2">{option.name} ({option.platform})</Typography>
                <Typography variant="caption" color="text.secondary">
                  Balance {option.balance}
                </Typography>
              </Box>
            </li>
          );
        }}
        renderInput={params => (
          <TextField {...params} label="Account" placeholder="Select a trading account"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {accountsLoading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }} />
        )}
        disabled={disabled || accountsLoading}
      />
      <TextField
        data-testid="abv-symbol-select"
        select
        size="small"
        sx={{ minWidth: 140 }}
        label="Symbol"
        value={effectiveSymbol ?? ''}
        onChange={event => chooseSymbol(event.target.value)}
        disabled={disabled || symbols.length === 0}
      >
        {symbols.map(item => (
          <MenuItem key={item} value={item}>{item}</MenuItem>
        ))}
      </TextField>
      <Chip size="small" color="primary" variant="outlined" label="Mode: Auto trading" data-testid="abv-mode-chip" />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }} data-testid="abv-operating-facts">
        {settings ? (
          <>
            {settings.fixed_risk_percent_per_trade != null
              ? <Chip size="small" variant="outlined" label={`Risk allocation: fixed ${settings.fixed_risk_percent_per_trade}%`} />
              : null}
            {settings.max_risk_percent_per_trade != null
              ? <Chip size="small" variant="outlined" label={`Max risk/trade: ${settings.max_risk_percent_per_trade}%`} />
              : null}
            {settings.min_reward_risk_ratio != null
              ? <Chip size="small" variant="outlined" label={`Reward:risk ≥ ${settings.min_reward_risk_ratio}:1`} />
              : null}
            {settings.order_type
              ? <Chip size="small" variant="outlined" label={`Action: ${settings.order_type}`} />
              : null}
            <Chip size="small" variant="outlined" label={
              profile?.runtime_model_default?.resolved_name
                ? `Runtime model: ${profile.runtime_model_default.resolved_name}`
                : `Runtime model: ${profile?.runtime_model_default?.intended_name ?? 'unavailable'}`}
              data-testid="abv-runtime-model-chip" />
            <Chip size="small" variant="outlined"
              label={`Pre-flight: ${settings.preflight && Object.keys(settings.preflight).length ? 'conditions on' : 'off'}`} />
            <Chip size="small" variant="outlined"
              label={profile?.installation ? `Installs ${profile.installation.is_active ? 'active' : 'inactive'}` : 'Install state: unavailable'} />
            <Chip size="small" variant="outlined"
              label={profile?.installation ? `Trade approval: ${profile.installation.require_approval ? 'on' : 'off'}` : 'Approval mode: unavailable'} />
            <Chip size="small" variant="outlined"
              label={profile?.installation ? `Scheduling: ${profile.installation.scheduling}` : 'Scheduling: unavailable'} />
          </>
        ) : (
          <Chip size="small" variant="outlined" color="warning" label="Operating settings: unavailable" />
        )}
      </Box>
      {!selectedAccount || !effectiveSymbol ? (
        <Typography variant="caption" color="text.secondary" data-testid="abv-selection-hint">
          Select an account and symbol — risk settings are supplied by the platform for this proof.
        </Typography>
      ) : null}
    </Box>
  );
};

export default ABVOperatingControls;
